import { supabase } from "@/integrations/supabase/client";
import { getAllDescendantFolderIds } from "@/stores/documents/repository/queries";
import type { TreeFolder } from "@/domains/documents/types";

export interface FolderSubtree {
  folderIds: string[];
  docIds: string[];
}

// Walk an in-memory folder tree to gather descendant folder IDs (excluding the root).
// Falls back to an empty list if the folder isn't found in the tree (e.g. tree
// hasn't loaded that branch yet) — callers should treat this as "no in-memory
// descendants" and fall back to a DB walk when the tree is incomplete.
export function collectDescendantFolderIdsFromTree(
  rootFolderId: string,
  findFolderById: (id: string) => TreeFolder | undefined
): string[] {
  const root = findFolderById(rootFolderId);
  if (!root || !root.children?.length) return [];
  const out: string[] = [];
  const stack: TreeFolder[] = [...root.children];
  while (stack.length > 0) {
    const f = stack.pop();
    if (!f) break;
    out.push(f.id);
    if (f.children?.length) stack.push(...f.children);
  }
  return out;
}

// Fetch the full subtree (descendants + self folder + all latest-version docs
// inside any of those folders) for cascading the selection state.
export async function fetchFolderSubtree(rootFolderId: string): Promise<FolderSubtree> {
  const allFolderIds = await getAllDescendantFolderIds([Number(rootFolderId)]);
  if (allFolderIds.length === 0) return { folderIds: [], docIds: [] };

  const { data: docs, error } = await supabase
    .from("documents")
    .select("id")
    .in("folder_id", allFolderIds)
    .eq("is_deleted", false)
    .eq("is_latest_version", true);

  if (error) throw error;

  return {
    // Strip the root — caller adds/removes it via its own selection state.
    folderIds: allFolderIds.filter((id) => id !== Number(rootFolderId)).map(String),
    docIds: (docs ?? []).map((d: { id: number }) => String(d.id)),
  };
}

// Reduce a list of folder IDs to top-level entries — i.e. drop any folder whose
// ancestor is also present. Used at action sites that recurse into subfolders
// internally (move, copy, trash) so we don't process the same subtree twice.
export function getTopLevelFolderIds(
  folderIds: string[],
  findFolderById: (id: string) => TreeFolder | undefined
): string[] {
  if (folderIds.length <= 1) return folderIds;
  const idSet = new Set(folderIds);
  return folderIds.filter((id) => {
    let current = findFolderById(id);
    while (current?.parentId) {
      if (idSet.has(current.parentId)) return false;
      current = findFolderById(current.parentId);
    }
    return true;
  });
}

// Filter doc IDs down to those NOT inside any of the given folder subtrees.
// Use at action sites where the folders are also being processed — avoids
// double-handling of docs the folder operation will already cover.
export async function excludeDocsInFolderSubtrees(
  docIds: string[],
  folderIds: string[]
): Promise<string[]> {
  if (docIds.length === 0 || folderIds.length === 0) return docIds;
  const subtreeFolderIds = await getAllDescendantFolderIds(folderIds.map(Number));
  const subtreeSet = new Set<number>(subtreeFolderIds);

  const { data, error } = await supabase
    .from("documents")
    .select("id, folder_id")
    .in("id", docIds.map(Number));

  if (error) throw error;

  return (data ?? [])
    .filter((d: { id: number; folder_id: number | null }) =>
      d.folder_id == null ? true : !subtreeSet.has(d.folder_id)
    )
    .map((d: { id: number }) => String(d.id));
}
